import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import {
  LINE_ITEM_TYPES,
  LINE_ITEM_TYPE_LABELS,
  type LineItemType,
} from '../../constants/line-item-types';
import {
  describeHiddenInUse,
  parseLineItemPrefs,
  parseLineItemPrefsSaveResult,
  type HiddenTypeInUse,
} from '../../lib/line-item-prefs';
import { LINE_ITEM_PREFS_QUERY_KEY } from '../../hooks/useLineItemPrefs';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';

/**
 * Which of the 22 line item types this company offers.
 *
 * The vocabulary is one enum for every tenant, enforced by the database, and
 * this page does not touch it. It edits a per-tenant HIDDEN set: a hidden type
 * leaves the ticket pickers and the Edit Pay Rates grid, and nothing else. An
 * old line item keeps its type and its label; a worker's rate on a hidden
 * type keeps paying, and the grid keeps showing it, flagged.
 *
 * ── THE WARNING IS NOT A REFUSAL ────────────────────────────────────────────
 * PUT /line-item-prefs saves the set as sent and then reports any newly hidden
 * type that still carries line items, with a count. The save has already
 * happened when that arrives. The page names the types and the counts so the
 * office knows what they just took out of the picker — it does not roll back,
 * and it does not ask first, because the API did neither.
 *
 * Modelled on `FieldTicketsPage`: dirty-gated Save, toast on result.
 */

const CODE_CLS = 'text-[11px] text-fieldiq-muted font-mono';

export default function LineItemTypesPage() {
  const queryClient = useQueryClient();
  // PUT /line-item-prefs is requireRole('admin'), non-strict.
  const canWrite = useCanWriteCompanySettings();

  const { data, isLoading, isError } = useQuery({
    queryKey: LINE_ITEM_PREFS_QUERY_KEY,
    queryFn: () => api.lineItemPrefs.get().then(r => parseLineItemPrefs(unwrap(r.data))),
    retry: false,
  });

  /** The hidden set as last saved — what "dirty" compares against. */
  const savedHidden = useMemo(() => new Set<LineItemType>(data?.hidden ?? []), [data]);
  const [hidden, setHidden] = useState<Set<LineItemType>>(new Set());
  const [inUse, setInUse] = useState<HiddenTypeInUse[]>([]);

  useEffect(() => {
    setHidden(new Set(savedHidden));
  }, [savedHidden]);

  const dirty = useMemo(() => {
    if (hidden.size !== savedHidden.size) return true;
    return Array.from(hidden).some(t => !savedHidden.has(t));
  }, [hidden, savedHidden]);

  const toggle = (type: LineItemType) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const saveMutation = useMutation({
    // Send in enum order so the request body reads the same as the list.
    mutationFn: () =>
      api.lineItemPrefs.update(LINE_ITEM_TYPES.filter(t => hidden.has(t))),
    onSuccess: (res) => {
      const result = parseLineItemPrefsSaveResult(unwrap(res.data));
      setInUse(result.inUse);
      queryClient.invalidateQueries({ queryKey: LINE_ITEM_PREFS_QUERY_KEY });
      toast.success('Line item types saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save line item types'),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  const offered = LINE_ITEM_TYPES.length - hidden.size;

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin can change which line item types are offered.</p>
        )}

        {isError && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-300" role="alert">
            Could not load this company's preferences. The list below shows every type as offered; saving will replace whatever is stored.
          </div>
        )}

        {inUse.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3" role="alert">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-300">Saved, but still in use: {describeHiddenInUse(inUse)}</p>
              <p className="text-amber-200/80 mt-1">
                Existing line items keep their type and pay rates keep paying. The type is only removed from the pickers and, where no rate exists, from the pay rate grid.
              </p>
            </div>
          </div>
        )}

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Offered types</h2>
            <span className="text-xs text-fieldiq-muted">{offered} of {LINE_ITEM_TYPES.length} offered</span>
          </div>
          <p className="text-xs text-fieldiq-muted">
            This list sets two pickers: the line item Type picker on tickets, and the pay rate grid on an employee's Edit Pay Rates. An unticked type leaves both. It stays valid on old tickets, and a worker's existing rate on it keeps paying and stays in the grid, flagged.
          </p>

          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
            {LINE_ITEM_TYPES.map(type => {
              const isOffered = !hidden.has(type);
              return (
                <label key={type} className={`flex items-center justify-between gap-4 px-4 py-3 ${canWrite ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                  <span className="flex flex-col min-w-0">
                    <span className={`text-sm font-medium ${isOffered ? 'text-white' : 'text-fieldiq-muted line-through'}`}>
                      {LINE_ITEM_TYPE_LABELS[type]}
                    </span>
                    <span className={CODE_CLS}>{type}</span>
                  </span>
                  <input
                    type="checkbox"
                    name={type}
                    aria-label={LINE_ITEM_TYPE_LABELS[type]}
                    checked={isOffered}
                    disabled={!canWrite}
                    onChange={() => toggle(type)}
                    className="h-4 w-4 accent-fieldiq-accent shrink-0"
                  />
                </label>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
